import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { getAvatarProps } from '../../../../models/AvatarUtils';
import { getDisplayName } from '../../../../models/NameUtils';
import { Avatar } from '../../../components-v2/Avatar';
import { Button } from '../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../components-v2/Dropdown';
import { Input } from '../../../components-v2/Input';
import { THEME } from '../../../components-v2/Theme/theme';

interface IProps {
	assignee: Api.IUser | undefined;
	users: Api.IUser[];
	onChange: (user: Api.IUser) => void;
	disabled?: boolean;
	showName?: boolean;
	label?: string;
}

const FILTER_THRESHOLD = 8;

export const AssigneeMenu: React.FC<IProps> = ({ assignee, users, onChange, disabled, showName, label }) => {
	const [filter, setFilter] = React.useState('');
	const normalizedFilter = filter.trim().toLowerCase();
	const visibleUsers = normalizedFilter
		? users.filter(user => getDisplayName(user).toLowerCase().includes(normalizedFilter))
		: users;
	const name = assignee ? getDisplayName(assignee) : 'Unassigned';

	return (
		<Dropdown onOpenChange={open => !open && setFilter('')}>
			<DropdownTrigger
				render={props => (
					<Button
						{...props}
						variant='ghost'
						size={showName || label ? 'sm' : 'icon'}
						disabled={disabled}
						aria-label={`Assigned to ${name}. Reassign`}
						styles={[styleSheet.trigger]}
						onClick={event => {
							event.stopPropagation();
							props.onClick?.(event);
						}}
					>
						{label ? (
							<span>{label}</span>
						) : (
							<>
								<Avatar styles={[styleSheet.avatar]} smallText {...getAvatarProps(assignee)} />
								{showName ? <span>{name}</span> : null}
							</>
						)}
					</Button>
				)}
			/>
			<DropdownContent align='end' styles={[styleSheet.content]} onClick={event => event.stopPropagation()}>
				{users.length > FILTER_THRESHOLD ? (
					<div className={css(styleSheet.filterRow)}>
						<Input
							autoFocus
							placeholder='Find a teammate…'
							value={filter}
							onChange={event => setFilter(event.currentTarget.value)}
							onKeyDown={event => event.stopPropagation()}
						/>
					</div>
				) : null}
				<div className={css(styleSheet.list)}>
					{visibleUsers.map(user => (
						<DropdownItem key={user.id} onClick={() => onChange(user)}>
							<span className={css(styleSheet.item, user.id === assignee?.id && styleSheet.selected)}>
								<Avatar styles={[styleSheet.avatar]} smallText {...getAvatarProps(user)} />
								{getDisplayName(user)}
							</span>
						</DropdownItem>
					))}
					{visibleUsers.length === 0 ? <span className={css(styleSheet.emptyText)}>No teammates match</span> : null}
				</div>
			</DropdownContent>
		</Dropdown>
	);
};

const styleSheet = StyleSheet.create({
	trigger: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		color: THEME.colors.text.light,
	},
	avatar: {
		height: 22,
		width: 22,
		flexShrink: 0,
	},
	content: {
		minWidth: 220,
	},
	filterRow: {
		padding: THEME.spacing.xs,
	},
	list: {
		maxHeight: 280,
		overflowY: 'auto',
	},
	item: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	selected: {
		fontWeight: THEME.typography.fontWeights.bold,
	},
	emptyText: {
		display: 'block',
		padding: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
});
